/**
 * نوار حکم — ناحیه‌ی اصلی صفحه‌ی نتیجه.
 *
 * ⚠️ **رنگ هرگز تنها حامل معنا نیست.** متن حکم همیشه نوشته می‌شود، پس فهم
 * پاسخ به تشخیص رنگ وابسته نیست. این یک الزام دسترس‌پذیری است.
 *
 * این تنها جایی است که مقیاس تایپوگرافی به بزرگ‌ترین اندازه‌ی صفحه می‌رسد و
 * حلقه‌ی اطمینان کنارش می‌نشیند: پاسخ اصلی باید از فاصله‌ی یک متری هم خوانده
 * شود، و بقیه‌ی صفحه پشتیبانِ همین یک خط است.
 */

import { CircleHelp, CircleMinus, CirclePlus, CircleSlash } from 'lucide-react'

import { Ring } from '@/components/ui/primitives'
import { cn } from '@/lib/cn'
import { fa } from '@/lib/format'
import { verdictHeadline, VERDICT_TONE } from '@/lib/labels'
import { useMeta } from '@/lib/meta-context'

import type { ConditionalDirection, Verdict } from '@/lib/types'

const ICONS = {
  support: CirclePlus,
  oppose: CircleMinus,
  conditional: CircleSlash,
  insufficient_information: CircleHelp,
} as const

/** زمینه‌ی هر حکم: هاله‌ی رنگی محو روی سطح برجسته، نه بلوک رنگی تخت. */
const GLOWS = {
  support: 'bg-support/18',
  oppose: 'bg-oppose/18',
  conditional: 'bg-conditional/20',
  neutral: 'bg-neutral/15',
} as const

const RINGS = {
  support: 'ring-support/25',
  oppose: 'ring-oppose/25',
  conditional: 'ring-conditional/30',
  neutral: 'ring-line',
} as const

const TEXTS = {
  support: 'text-support',
  oppose: 'text-oppose',
  conditional: 'text-conditional',
  neutral: 'text-ink-muted',
} as const

const CHIPS = {
  support: 'bg-support-soft text-support ring-support/25',
  oppose: 'bg-oppose-soft text-oppose ring-oppose/25',
  conditional: 'bg-conditional-soft text-conditional ring-conditional/30',
  neutral: 'bg-surface-sunken text-ink-muted ring-line',
} as const

interface VerdictBandProps {
  verdict: Verdict
  /** جهت حکم مشروط. سرتیتر «مخالفت مشروط» می‌شود نه «مشروط». */
  direction?: ConditionalDirection
  confidence: number
  /** شماره‌ی تلاش. بیش از یک، یعنی این یک بازسازی است. */
  attempt?: number
}

export const VerdictBand = ({
  verdict,
  direction = '',
  confidence,
  attempt = 1,
}: VerdictBandProps) => {
  const { meta } = useMeta()
  const tone = VERDICT_TONE[verdict]
  const Icon = ICONS[verdict]

  return (
    <section
      className={cn(
        'animate-rise relative overflow-hidden rounded-lg bg-surface-raised p-6 shadow-lifted ring-1',
        RINGS[tone],
      )}
    >
      {/* هاله‌ی گوشه، رنگ حکم را بدون رنگ‌آمیزی کل بلوک می‌رساند. */}
      <span
        className={cn(
          'pointer-events-none absolute -top-28 -end-20 size-72 rounded-full blur-3xl',
          GLOWS[tone],
        )}
        aria-hidden
      />

      <div className="relative flex flex-wrap items-center justify-between gap-6">
        <div className="flex min-w-0 items-center gap-4">
          <span
            className={cn(
              'grid size-12 shrink-0 place-items-center rounded-md ring-1 ring-inset',
              CHIPS[tone],
            )}
            aria-hidden
          >
            <Icon className="size-6" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium text-ink-muted">تصمیم محتمل</p>
            <p
              className={cn(
                'mt-0.5 text-2xl leading-tight font-bold tracking-tight sm:text-3xl',
                TEXTS[tone],
              )}
            >
              {verdictHeadline(meta.labels, verdict, direction)}
            </p>
            {attempt > 1 && (
              <p className="mt-1.5 text-xs text-ink-faint">
                نتیجه‌ی تلاش شماره‌ی {fa(attempt)}
              </p>
            )}
          </div>
        </div>

        <Ring value={confidence} label="اطمینان" size={96} />
      </div>
    </section>
  )
}
